import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppState } from '@/store';
import React from 'react';

interface MentionRendererProps {
  content: string;
  className?: string;
}

/**
 * Component that parses HTML content with mentions and renders them as clickable links
 * Handles TipTap mention format: <span class="mention" data-id="userId" data-type="mention" data-label="Name">@Name</span>
 * Also handles text-based mentions: @[user_id:user_type:full_name] or plain @FullName
 */
export function MentionRenderer({ content, className = '' }: MentionRendererProps) {
  const navigate = useNavigate();
  const userData = useSelector((state: AppState) => state.auth.userData);
  const currentUser = userData?.user;

  const handleMentionClick = (userId: string, userType?: string, organizationId?: string) => {
    const currentUserType = currentUser?.type;

    // If we have the userType, use it directly
    if (userType) {
      if (currentUserType === 'job_seeker') {
        if (userType === 'job_seeker') {
          // For job_seeker type, use userId
          navigate(`/jobseeker/users/${userId}`);
        } else if (userType === 'employer') {
          // For employer type, use organization_id
          if (organizationId) {
            navigate(`/jobseeker/employers/${organizationId}`);
          }
        } else {
          navigate(`/jobseeker/employers/${userId}`);
        }
      } else if (currentUserType === 'employer') {
        if (userType === 'employer') {
          // For employer type, use organization_id
          if (organizationId) {
            navigate(`/employer/organizations/${organizationId}`);
          }
        } else if (userType === 'job_seeker') {
          // For job_seeker type, use userId
          navigate(`/employer/jobseekers/${userId}`);
        } else {
          navigate(`/employer/organizations/${userId}`);
        }
      }
    } else {
      // Fallback: infer type from current user's perspective
      if (currentUserType === 'job_seeker') {
        navigate(`/jobseeker/employers/${userId}`);
      } else if (currentUserType === 'employer') {
        navigate(`/employer/jobseekers/${userId}`);
      }
    }
  };

  const parseHTMLContent = (htmlString: string): React.ReactNode[] => {
    if (!htmlString) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const nodes: React.ReactNode[] = [];

    const processNode = (node: Node, key: string): React.ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;

        // Handle mention spans
        if (element.classList.contains('mention') && element.hasAttribute('data-id')) {
          const userId = element.getAttribute('data-id') || '';
          const label = element.getAttribute('data-label') || element.textContent || '';

          // Try to get user type from various possible sources
          let userType = element.getAttribute('data-user-type') || element.getAttribute('data-type');

          // Get organization_id if available
          const organizationId = element.getAttribute('data-organization-id') || undefined;

          // If data-type is just "mention", try to infer from other attributes or context
          if (userType === 'mention') {
            userType = undefined;
          }

          return (
            <span
              key={key}
              onClick={(e) => {
                e.stopPropagation();
                handleMentionClick(userId, userType || undefined, organizationId);
              }}
              className="font-semibold text-primary cursor-pointer hover:underline"
            >
              {label}
            </span>
          );
        }

        // Handle paragraph tags
        if (element.tagName.toLowerCase() === 'p') {
          const children = Array.from(element.childNodes).map((child, idx) =>
            processNode(child, `${key}-p-${idx}`)
          );
          return <p key={key}>{children}</p>;
        }

        // Handle br tags
        if (element.tagName.toLowerCase() === 'br') {
          return <br key={key} />;
        }

        // Handle unordered lists
        if (element.tagName.toLowerCase() === 'ul') {
          const children = Array.from(element.childNodes).map((child, idx) =>
            processNode(child, `${key}-ul-${idx}`)
          );
          return <ul key={key} className="list-disc ml-4 my-2">{children}</ul>;
        }

        // Handle ordered lists
        if (element.tagName.toLowerCase() === 'ol') {
          const children = Array.from(element.childNodes).map((child, idx) =>
            processNode(child, `${key}-ol-${idx}`)
          );
          return <ol key={key} className="list-decimal ml-4 my-2">{children}</ol>;
        }

        // Handle list items
        if (element.tagName.toLowerCase() === 'li') {
          const children = Array.from(element.childNodes).map((child, idx) =>
            processNode(child, `${key}-li-${idx}`)
          );
          return <li key={key}>{children}</li>;
        }

        // Handle anchor/link tags
        if (element.tagName.toLowerCase() === 'a') {
          const href = element.getAttribute('href') || '#';
          const target = element.getAttribute('target') || '_blank';
          const children = Array.from(element.childNodes).map((child, idx) =>
            processNode(child, `${key}-a-${idx}`)
          );
          return (
            <a
              key={key}
              href={href}
              target={target}
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-primary underline underline-offset-2 hover:text-primary/80"
            >
              {children}
            </a>
          );
        }

        // Handle other inline elements (span, strong, em, etc.)
        const children = Array.from(element.childNodes).map((child, idx) =>
          processNode(child, `${key}-${element.tagName}-${idx}`)
        );

        // Return wrapped in appropriate element
        switch (element.tagName.toLowerCase()) {
          case 'strong':
          case 'b':
            return <strong key={key}>{children}</strong>;
          case 'em':
          case 'i':
            return <em key={key}>{children}</em>;
          case 'u':
            return <u key={key}>{children}</u>;
          case 'span':
            return <span key={key} className={element.className}>{children}</span>;
          default:
            return <React.Fragment key={key}>{children}</React.Fragment>;
        }
      }

      return null;
    };

    // Process body children
    Array.from(doc.body.childNodes).forEach((node, idx) => {
      const processed = processNode(node, `node-${idx}`);
      if (processed !== null) {
        nodes.push(processed);
      }
    });

    return nodes;
  };

  // Unescape HTML if it's been JSON stringified
  const unescapeContent = (text: string): string => {
    if (!text) return text;

    // Check if content appears to be JSON-escaped (has escaped quotes)
    if (text.includes('\\"')) {
      try {
        // Try to parse as JSON string
        return JSON.parse(`"${text}"`);
      } catch {
        // If parsing fails, manually unescape common patterns
        return text.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
      }
    }

    return text;
  };

  const processedContent = unescapeContent(content);

  // Check if content is HTML or plain text
  const isHTML = /<[^>]+>/g.test(processedContent);

  return (
    <div className={`whitespace-break-spaces ${className}`}>
      {isHTML ? parseHTMLContent(processedContent) : processedContent}
    </div>
  );
}

/**
 * Utility function to format mentions in the backend format
 * Call this when creating/editing posts, comments, or replies
 */
export function formatMentionsForBackend(
  content: string,
  mentionedUsers: Array<{ id: string; full_name: string; type: string }>
): string {
  if (!mentionedUsers || mentionedUsers.length === 0) return content;

  let formattedContent = content;

  // Replace @FullName with @[user_id:user_type:full_name]
  mentionedUsers.forEach((user) => {
    const plainMention = `@${user.full_name}`;
    const structuredMention = `@[${user.id}:${user.type}:${user.full_name}]`;

    // Replace all instances of the plain mention
    formattedContent = formattedContent.replace(
      new RegExp(plainMention.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      structuredMention
    );
  });

  return formattedContent;
}
